import { formatMoney } from '@domain';
import { can } from '@domain';
import { Card, buttonCard, buttonPrimary, pillStyle } from '../components/ui.js';
import type { Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import type { Action, AppState, Role } from '../state.js';
import type { ListColumn, ListFixture } from '../types.js';

const NUMERIC_KEYS = ['amount', 'outstanding', 'owing', 'owed', 'value', 'buy', 'sell', 'onhand', 'reorder', 'cover'];
const WHOLE_NUMBER_KEYS = ['onhand', 'reorder', 'cover'];

const REFRESH_COLUMN: ListColumn = { key: '__refresh', label: '', w: '34px', align: 'center' };
const PDF_COLUMN: ListColumn = { key: '__pdf', label: '', w: '34px', align: 'center' };
const PRINT_COLUMN: ListColumn = { key: '__print', label: '', w: '34px', align: 'center' };

interface ListScreenProps {
  listKey: string;
  list: ListFixture;
  state: AppState;
  role: Role;
  rtl: boolean;
  t: Translate;
  dispatch: (action: Action) => void;
  onReadRow: (key: string, current: string) => void;
  /** Ask Xero again. The screen already does on every visit; this is for
      the moment somebody changes something in Xero and comes straight back. */
  onRefresh: () => void;
  refreshing: boolean;
}

/**
 * Which lists hold something Xero will render as a PDF.
 *
 * Contacts and items are records, not documents: there is nothing to print and
 * offering a button would only produce a refusal.
 */
/** Lists of people and companies, which can be brought in from a file. */
const CONTACT_LISTS = new Set(['customers', 'suppliers']);

const PDF_KINDS: Record<string, string> = {
  invoices: 'invoice',
  bills: 'bill',
  quotes: 'quote',
  pos: 'po',
};

export function ListScreen({
  listKey,
  list,
  state,
  role,
  rtl,
  t,
  dispatch,
  onReadRow,
  onRefresh,
  refreshing,
}: ListScreenProps) {
  const isAdmin = can(role, 'operate');
  // A PDF column only where Xero has something to render, and only when the
  // list actually carried ids — an older response without them would give every
  // row a button that could not work.
  const hasPdf = Boolean(PDF_KINDS[listKey]) && Array.isArray(list.ids);
  const columns = [
    ...list.columns,
    ...(hasPdf ? [PDF_COLUMN, PRINT_COLUMN] : []),
    ...(isAdmin ? [REFRESH_COLUMN] : []),
  ];
  const grid = columns.map((c) => c.w).join(' ');
  const view = state.listView[listKey] ?? list.views[0] ?? 'All';
  const pillIndex = list.columns.findIndex((c) => c.pill);

  // Each row is paired with its Xero identifier BEFORE any filtering. Indexing
  // the two lists separately worked only while every row was shown: choose a
  // view, and row three of the filtered list would print document three of the
  // unfiltered one. The wrong invoice, confidently.
  const entries = list.rows.map((row, index) => ({ row, xeroId: list.ids?.[index] ?? '' }));

  const visible =
    view === list.views[0] || pillIndex < 0
      ? entries
      : entries.filter(({ row }) => {
          const status = String(row[pillIndex]).toUpperCase();
          return status === view.toUpperCase() || (view === 'Queued' && status === 'REVIEW');
        });

  // Contact-style lists open the glance drawer. Document lists open the
  // document, which is where its lines are and where it can be paid.
  const opensGlance = list.columns[0]?.key === 'name';
  const documentKind = PDF_KINDS[listKey];

  let total = 0;
  const align = (column: ListColumn) =>
    column.align === 'end' ? (rtl ? 'left' : 'right') : column.align === 'center' ? 'center' : 'start';

  return (
    <div style={{ padding: '22px 24px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
        <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 600, letterSpacing: '-.01em' }}>{t(list.title)}</h1>
        <span
          style={{
            fontFamily: theme.fontMono,
            fontSize: '12px',
            color: '#5A6676',
            background: '#F1F1F3',
            borderRadius: theme.radiusPill,
            padding: '3px 9px',
          }}
        >
          {visible.length} / {list.rows.length}
        </span>
        {refreshing && (
          <span style={{ fontSize: '12px', color: theme.textMuted }}>{t('Reading Xero…')}</span>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={onRefresh} disabled={refreshing} style={buttonCard}>
          {t('Refresh')}
        </button>
        <button style={buttonCard}>{t('Export')}</button>
        {/*
          Contacts arrive in spreadsheets far more often than one at a time,
          so the choice sits next to the manual form rather than behind it.
        */}
        {CONTACT_LISTS.has(listKey) && isAdmin && (
          <button
            onClick={() => dispatch({ type: 'go', screen: `import:${listKey}` })}
            style={{ ...buttonCard, padding: '9px 15px', fontSize: '12.5px' }}
          >
            {t('Import')}
          </button>
        )}
        {list.newLabel && (
          <button onClick={() => dispatch({ type: 'go', screen: list.newScreen })} style={{ ...buttonPrimary, padding: '9px 15px', fontSize: '12.5px' }}>
            {t(list.newLabel)}
          </button>
        )}
      </div>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '0 8px', borderBottom: `1px solid ${theme.dividerSoft}` }}>
          {list.views.map((v) => (
            <button
              key={v}
              onClick={() => dispatch({ type: 'setListView', list: listKey, view: v })}
              style={{
                background: 'transparent',
                border: 0,
                borderBottom: `2px solid ${v === view ? theme.accent : 'transparent'}`,
                padding: '12px 12px 10px',
                fontSize: '12.5px',
                fontWeight: v === view ? 600 : 500,
                color: v === view ? theme.text : theme.textMuted,
                cursor: 'pointer',
              }}
            >
              {t(v)}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              border: `1px solid ${theme.border}`,
              borderRadius: theme.radiusControl,
              padding: '6px 9px',
              background: theme.surfaceMuted,
              margin: '8px 4px',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={theme.textSubtle} strokeWidth="2">
              <path d="M4 6h16M7 12h10M10 18h4" />
            </svg>
            <span style={{ fontSize: '12px', color: theme.textMuted }}>{t('Filter')}</span>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 'max-content' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: grid,
                gap: '12px',
                padding: '9px 16px',
                background: theme.surfaceMuted,
                borderBottom: `1px solid ${theme.dividerSoft}`,
                fontSize: '10.5px',
                fontWeight: 500,
                letterSpacing: '.11em',
                textTransform: 'uppercase',
                color: '#5A6676',
                fontFamily: theme.fontMono,
              }}
            >
              {columns.map((column, i) => (
                <div key={`${column.key}-${i}`} style={{ textAlign: align(column) }}>
                  {column.label ? t(column.label) : ''}
                </div>
              ))}
            </div>

            {visible.map(({ row, xeroId }) => {
              const rowId = String(row[0]);
              const readKey = `${listKey}:${rowId}`;
              // Only documents Xero renders have a PDF. A contact does not.
              const pdfKind = PDF_KINDS[listKey];
              return (
                <div
                  key={rowId}
                  onClick={
                    opensGlance
                      ? () => dispatch({ type: 'openGlance', target: { name: rowId, acct: String(row[1] ?? '') } })
                      : documentKind && xeroId
                        ? () => dispatch({ type: 'go', screen: `doc:${documentKind}:${xeroId}` })
                        : undefined
                  }
                  style={{
                    display: 'grid',
                    gridTemplateColumns: grid,
                    gap: '12px',
                    alignItems: 'center',
                    padding: '11px 16px',
                    borderBottom: `1px solid ${theme.divider}`,
                    fontSize: '12.5px',
                    cursor: opensGlance || (documentKind && xeroId) ? 'pointer' : 'default',
                    background: theme.surface,
                  }}
                >
                  {columns.map((column, i) => {
                    if (column.key === '__print') {
                      return (
                        <div key="__print" style={{ display: 'grid', placeItems: 'center' }}>
                          {xeroId ? (
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                dispatch({ type: 'go', screen: `print:${pdfKind}:${xeroId}` });
                              }}
                              title={t('Print our own bilingual layout, with the logo set here')}
                              aria-label={t('Print')}
                              style={{ width: '26px', height: '26px', border: 0, background: 'transparent', borderRadius: '8px', color: theme.textMuted, cursor: 'pointer', display: 'grid', placeItems: 'center' }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M6 9V3h12v6" />
                                <path d="M6 18H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" />
                                <path d="M6 14h12v7H6z" />
                              </svg>
                            </button>
                          ) : null}
                        </div>
                      );
                    }
                    if (column.key === '__pdf') {
                      return (
                        <div key="__pdf" style={{ display: 'grid', placeItems: 'center' }}>
                          {xeroId ? (
                            <a
                              href={`/api/documents/${pdfKind}/${xeroId}/pdf`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(event) => event.stopPropagation()}
                              title={t('Open the PDF Xero produces, with this company’s own branding')}
                              aria-label={t('PDF')}
                              style={{ display: 'grid', placeItems: 'center', width: '26px', height: '26px', borderRadius: '8px', color: theme.textMuted }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                                <path d="M14 3v5h5" />
                              </svg>
                            </a>
                          ) : null}
                        </div>
                      );
                    }
                    if (column.key === '__refresh') {
                      const current = String(row[pillIndex] ?? 'DRAFT').toUpperCase();
                      return (
                        <div key="__refresh" style={{ display: 'grid', placeItems: 'center' }}>
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              onReadRow(readKey, current);
                            }}
                            title={t('Read this record from Xero')}
                            style={{
                              width: '24px',
                              height: '24px',
                              border: 0,
                              background: 'transparent',
                              borderRadius: theme.radiusPill,
                              color: theme.textSubtle,
                              cursor: 'pointer',
                              display: 'grid',
                              placeItems: 'center',
                            }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                              <path d="M20 12a8 8 0 1 1-2.3-5.6" />
                              <path d="M20 4v4h-4" />
                            </svg>
                          </button>
                        </div>
                      );
                    }

                    const raw = column.pill && state.reads[readKey] ? state.reads[readKey]! : row[i];
                    const isNumeric = NUMERIC_KEYS.includes(column.key) && typeof raw === 'number';
                    if (column.key === 'amount' || column.key === 'value') {
                      total += typeof raw === 'number' ? raw : 0;
                    }

                    const text = isNumeric
                      ? WHOLE_NUMBER_KEYS.includes(column.key)
                        ? String(raw)
                        : formatMoney(raw as number)
                      : column.pill
                        ? t(String(raw))
                        : String(raw);

                    return (
                      <div
                        key={`${column.key}-${i}`}
                        style={{
                          textAlign: align(column),
                          fontFamily: column.mono ? theme.fontMono : 'inherit',
                          color: column.link ? theme.ink : column.muted ? theme.textMuted : theme.text,
                          fontWeight: column.link ? 500 : 400,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {column.pill ? <span style={pillStyle(String(raw))}>{text}</span> : text}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '10px 14px',
            padding: '12px 16px',
            background: theme.surfaceMuted,
            fontSize: '12.5px',
            color: theme.textMuted,
          }}
        >
          <span>
            {visible.length} {t('rows from Xero')}
          </span>
          <span style={{ color: theme.textSubtle }}>{t('Xero status is read one record at a time — press ↻ on a row.')}</span>
          <div style={{ flex: 1 }} />
          {total > 0 && (
            <span style={{ fontFamily: theme.fontMono, color: theme.text, fontWeight: 500 }}>QAR {formatMoney(total)}</span>
          )}
        </div>
      </Card>
    </div>
  );
}
